import { Box, Button } from '@mui/material';
import MuseFlowCursor from 'Components/Cursor';
import { Menu } from 'Components/RoadmapComponents/Menu';
import { useAudioRecognition } from 'AudioRecognition';
import type { RecognitionFrame } from 'AudioRecognition/types';
import { midiToName } from 'AudioRecognition/util/note';
import { useEffect, useMemo, useRef, useState, useCallback, type RefObject } from 'react';
import {
  useMidiContext,
  type Listener,
  type PreferredInputMode,
  type PedalMessage,
} from 'Contexts/MidiContext';
import { FeatureFlag, useFeatureFlag } from 'Utils/FeatureFlags';
import { createOSMD } from 'Utils/OSMDUtility';
import * as Tone from 'tone';
import { OpenSheetMusicDisplay as OSMD } from '@museflow-ai/opensheetmusicdisplay';
import PlayIcon from 'assets/images/Play.png';
import PauseIcon from 'assets/images/Pause.png';
import RestartIcon from 'assets/images/Repertoire/Restart.png';
import PageNav from './PageNav';
import PlaybackTempoControl from './components/PlaybackTempoControl';
import useDraftPlaybackTimeKeeper from './components/useDraftPlaybackTimeKeeper';
import { transportPlayButtonChrome, transportRestartButtonChrome } from './shared/playbackChrome';
import {
  buildLiveComposerMusicXml,
  type LiveComposerEvent,
  type LiveComposerPedalEvent,
} from './CreateMusicXml';

const AUDIO_LISTENER_NAME = 'repertoire-create-audio-listener';
const MIDI_LISTENER_NAME = 'repertoire-create-midi-listener';
const CHORD_GROUP_WINDOW_MS = 140;
const AUDIO_ONSET_DEDUP_MS = 260;
const AUDIO_RELEASE_GRACE_MS = 180;
const DEFAULT_PLAYBACK_TEMPO_BPM = 104;
const MIN_PLAYBACK_TEMPO_BPM = 40;
const MAX_PLAYBACK_TEMPO_BPM = 220;
const MIN_EVENT_DURATION_DIVISIONS = 1;
const DIVISIONS_PER_BEAT = 4;
const PEDAL_LABEL_DOWN = 'Pedal Down';
const PEDAL_LABEL_UP = 'Pedal Up';

type ComposerInputMode = PreferredInputMode | null;
type PlaybackSamplerWindow = Window & {
  __ensureSamplerReady?: (forceRebuild?: boolean) => Promise<void>;
  __museflowSampler?: Tone.Sampler;
};

type PendingChord = {
  startedAtMs: number;
  midiNotes: Set<number>;
  source: 'midi' | 'audio';
  timeoutId: number;
};

type ActiveCapturedEvent = {
  id: string;
  startedAtMs: number;
  source: 'midi' | 'audio';
  allNotes: Set<number>;
  activeNotes: Set<number>;
};

type PlaybackInstruction =
  | {
      id: string;
      atDivisions: number;
      type: 'pedal';
      pedalDown: boolean;
    }
  | {
      id: string;
      atDivisions: number;
      type: 'note-on' | 'note-off';
      midiNotes: number[];
      source: 'midi' | 'audio';
    };

const normalizeMidiNotes = (notes: number[]) =>
  Array.from(new Set(notes.map((note) => Math.round(note))))
    .filter((note) => note >= 0 && note <= 127)
    .sort((a, b) => a - b);

const formatChordLabel = (midiNotes: number[]) =>
  normalizeMidiNotes(midiNotes).map(midiToName).join(' + ');

const getPlaybackBeatDurationSeconds = (tempoBpm: number) => 60 / Math.max(tempoBpm, 1);

const getDivisionDurationSeconds = (tempoBpm: number) =>
  getPlaybackBeatDurationSeconds(tempoBpm) / DIVISIONS_PER_BEAT;

const LiveComposerSheet = ({
  musicXml,
  measureCount,
  noteCount,
  playbackTempo,
  canPlay,
  isPlayingBack,
  showPlaybackCursor,
  onTempoChange,
  onPlaybackToggle,
  onPlaybackRestart,
}: {
  musicXml: string;
  measureCount: number;
  noteCount: number;
  playbackTempo: number;
  canPlay: boolean;
  isPlayingBack: boolean;
  showPlaybackCursor: boolean;
  onTempoChange: (tempo: number) => void;
  onPlaybackToggle: () => void;
  onPlaybackRestart: () => void;
}) => {
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const osmdRef = useRef<OSMD | null>(null);
  const [osmdInstance, setOsmdInstance] = useState<OSMD | null>(null);
  const [isScoreReady, setIsScoreReady] = useState(false);
  const [renderError, setRenderError] = useState<string | null>(null);
  const playbackTimeKeeper = useDraftPlaybackTimeKeeper({
    bpm: playbackTempo,
    isPlaying: isPlayingBack,
    cursorIsVisible: showPlaybackCursor,
  });

  useEffect(() => {
    if (!sheetRef.current) return;

    const osmd = createOSMD(sheetRef as RefObject<HTMLDivElement>, true);
    osmd.EngravingRules.PageTopMargin = 2;
    osmd.EngravingRules.PageBottomMargin = 2;
    osmd.EngravingRules.PageLeftMargin = 1;
    osmd.EngravingRules.PageRightMargin = 1;
    osmd.EngravingRules.SystemLeftMargin = 0;
    osmd.EngravingRules.SystemRightMargin = 0;
    osmd.EngravingRules.StaffDistance = 3;
    osmd.EngravingRules.BetweenStaffDistance = 2.5;
    osmdRef.current = osmd;
    setOsmdInstance(osmd);

    return () => {
      osmdRef.current = null;
      setOsmdInstance(null);
      if (sheetRef.current) {
        sheetRef.current.innerHTML = '';
      }
    };
  }, []);

  useEffect(() => {
    const osmd = osmdRef.current;
    if (!osmd) return;

    let cancelled = false;
    setRenderError(null);
    setIsScoreReady(false);

    const render = async () => {
      try {
        await osmd.load(musicXml);
        if (cancelled) return;
        osmd.render();
        setIsScoreReady(true);
      } catch (error) {
        if (cancelled) return;
        setRenderError(error instanceof Error ? error.message : 'Failed to render score');
      }
    };

    void render();

    return () => {
      cancelled = true;
      setIsScoreReady(false);
    };
  }, [musicXml]);

  return (
    <Box
      sx={{
        borderRadius: '24px',
        background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,249,253,1) 100%)',
        boxShadow: '0 24px 52px rgba(0, 0, 0, 0.18)',
        minHeight: '720px',
        border: '1px solid rgba(10, 15, 26, 0.08)',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          borderBottom: '1px solid rgba(9, 19, 36, 0.08)',
          padding: '18px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Box
            sx={{
              color: '#111827',
              fontSize: '20px',
              fontWeight: 700,
              letterSpacing: '0.01em',
            }}
          >
            Live Repertoire Draft
          </Box>
          <Box
            sx={{
              color: 'rgba(17, 24, 39, 0.64)',
              fontSize: '12px',
              marginTop: '4px',
            }}
          >
            Quantized draft notation. Notes, rests, and pedal are placed on a sixteenth-note grid in
            4/4.
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            flexWrap: 'wrap',
          }}
        >
          <PlaybackTempoControl
            tempo={playbackTempo}
            defaultTempo={DEFAULT_PLAYBACK_TEMPO_BPM}
            minTempo={MIN_PLAYBACK_TEMPO_BPM}
            maxTempo={MAX_PLAYBACK_TEMPO_BPM}
            onTempoChange={onTempoChange}
          />
          <StatPill label="Measures" value={`${measureCount}`} />
          <StatPill label="Captured events" value={`${noteCount}`} />
        </Box>
      </Box>

      {renderError && (
        <Box
          sx={{
            padding: '12px 24px',
            color: '#8B1E1E',
            backgroundColor: 'rgba(189, 57, 57, 0.08)',
            borderBottom: '1px solid rgba(189, 57, 57, 0.12)',
            fontSize: '13px',
          }}
        >
          {renderError}
        </Box>
      )}

      <Box
        sx={{
          padding: { xs: '18px 14px 28px', md: '26px 28px 36px' },
          overflowX: 'auto',
          minHeight: '560px',
          position: 'relative',
          '& svg': {
            maxWidth: '100%',
            height: 'auto',
          },
        }}
      >
        <Box
          ref={sheetRef}
          sx={{
            minWidth: '720px',
            color: '#111827',
          }}
        />
        {playbackTimeKeeper && isScoreReady && (
          <MuseFlowCursor
            timeKeeper={playbackTimeKeeper}
            startTimestamp={0}
            osmdMeasureDuration={1}
            measureCount={measureCount}
            osmd={osmdInstance}
            isPlaying={isPlayingBack}
            countInTimestamp={0}
            cursorIsVisible={showPlaybackCursor}
          />
        )}
      </Box>

      <Box
        sx={{
          borderTop: '1px solid rgba(9, 19, 36, 0.08)',
          padding: '18px 24px 24px',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'flex-end',
          minHeight: '118px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: '18px',
          }}
        >
          <Box
            onClick={canPlay ? onPlaybackRestart : undefined}
            aria-label="Replay draft from the beginning"
            sx={{
              ...transportRestartButtonChrome,
              opacity: canPlay ? 1 : 0.42,
              pointerEvents: canPlay ? 'auto' : 'none',
            }}
          >
            <img style={{ maxWidth: '100%' }} src={RestartIcon} />
          </Box>
          <Box
            onClick={canPlay ? onPlaybackToggle : undefined}
            aria-label={isPlayingBack ? 'Pause draft playback' : 'Play draft playback'}
            sx={{
              ...transportPlayButtonChrome,
              opacity: canPlay ? 1 : 0.42,
              pointerEvents: canPlay ? 'auto' : 'none',
            }}
          >
            <img
              style={{ maxWidth: '100%', maxHeight: '100%', margin: '0px' }}
              src={isPlayingBack ? PauseIcon : PlayIcon}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const Create = () => {
  const audioRecognitionEnabled = useFeatureFlag(FeatureFlag.USE_AUDIO_RECOGNITION);
  const {
    addListener: addMidiListener,
    removeListener: removeMidiListener,
    preferredInputMode,
  } = useMidiContext();
  const {
    addListener: addAudioListener,
    removeListener: removeAudioListener,
    start,
    stop,
    setRecognitionHints,
  } = useAudioRecognition();

  const [inputMode, setInputMode] = useState<ComposerInputMode>(preferredInputMode);
  const [capturedEvents, setCapturedEvents] = useState<LiveComposerEvent[]>([]);
  const [capturedPedalEvents, setCapturedPedalEvents] = useState<LiveComposerPedalEvent[]>([]);
  const [isPlayingBack, setIsPlayingBack] = useState(false);
  const [playbackTempo, setPlaybackTempo] = useState(DEFAULT_PLAYBACK_TEMPO_BPM);
  const [showPlaybackCursor, setShowPlaybackCursor] = useState(false);

  const nextEventIdRef = useRef(1);
  const pendingChordRef = useRef<PendingChord | null>(null);
  const activeCapturedEventsRef = useRef<Map<string, ActiveCapturedEvent>>(new Map());
  const activeNoteOwnerRef = useRef<Map<number, string>>(new Map());
  const recentAudioOnsetsRef = useRef<Map<number, number>>(new Map());
  const audioMissingSinceRef = useRef<Map<number, number>>(new Map());
  const audioStartRequestRef = useRef(0);
  const hasInitializedInputModeRef = useRef(false);
  const lastReleasedAtMsRef = useRef<number | null>(null);
  const playbackSynthRef = useRef<Tone.PolySynth<Tone.Synth> | null>(null);
  const playbackInstructionsRef = useRef<PlaybackInstruction[]>([]);
  const playbackSnapshotSignatureRef = useRef('');
  const playbackNextInstructionIndexRef = useRef(0);
  const playbackNextInstructionTimeoutRef = useRef<number | null>(null);
  const playbackNextInstructionDueAtMsRef = useRef<number | null>(null);
  const playbackResumeDelayMsRef = useRef(0);
  const playbackTempoRef = useRef(playbackTempo);
  const playbackPedalDownRef = useRef(false);
  const playbackSustainedNotesRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    playbackTempoRef.current = playbackTempo;
  }, [playbackTempo]);

  const quantizeDurationDivisions = useCallback((durationMs: number) => {
    const sixteenthMs = 60_000 / Math.max(playbackTempoRef.current, 1) / DIVISIONS_PER_BEAT;
    return Math.max(
      MIN_EVENT_DURATION_DIVISIONS,
      Math.round(durationMs / Math.max(sixteenthMs, 1)),
    );
  }, []);

  const quantizeRestDivisions = useCallback((durationMs: number) => {
    const sixteenthMs = 60_000 / Math.max(playbackTempoRef.current, 1) / DIVISIONS_PER_BEAT;
    return Math.max(0, Math.round(durationMs / Math.max(sixteenthMs, 1)));
  }, []);

  const clearActiveCaptureState = useCallback(() => {
    activeCapturedEventsRef.current.clear();
    activeNoteOwnerRef.current.clear();
    audioMissingSinceRef.current.clear();
    lastReleasedAtMsRef.current = null;
  }, []);

  const computeRestBeforeDivisions = useCallback(
    (capturedAtMs: number) => {
      if (activeCapturedEventsRef.current.size > 0) return 0;
      const lastReleasedAtMs = lastReleasedAtMsRef.current;
      if (lastReleasedAtMs == null || capturedAtMs <= lastReleasedAtMs) return 0;
      return quantizeRestDivisions(capturedAtMs - lastReleasedAtMs);
    },
    [quantizeRestDivisions],
  );

  const registerCapturedEvent = useCallback(
    (midiNotes: number[], capturedAtMs: number, source: 'midi' | 'audio') => {
      const id = `live-composer-${nextEventIdRef.current++}`;
      const normalizedMidiNotes = normalizeMidiNotes(midiNotes);
      const restBeforeDivisions = computeRestBeforeDivisions(capturedAtMs);

      setCapturedEvents((prev) => [
        ...prev,
        {
          id,
          midiNotes: normalizedMidiNotes,
          capturedAtMs,
          source,
          restBeforeDivisions,
          durationDivisions: MIN_EVENT_DURATION_DIVISIONS,
        },
      ]);

      const activeEvent: ActiveCapturedEvent = {
        id,
        startedAtMs: capturedAtMs,
        source,
        allNotes: new Set(normalizedMidiNotes),
        activeNotes: new Set(normalizedMidiNotes),
      };

      activeCapturedEventsRef.current.set(id, activeEvent);
      normalizedMidiNotes.forEach((note) => {
        activeNoteOwnerRef.current.set(note, id);
        audioMissingSinceRef.current.delete(note);
      });
    },
    [computeRestBeforeDivisions],
  );

  const finalizeCapturedEvent = useCallback(
    (eventId: string, endedAtMs: number) => {
      const activeEvent = activeCapturedEventsRef.current.get(eventId);
      if (!activeEvent) return;

      const durationDivisions = quantizeDurationDivisions(endedAtMs - activeEvent.startedAtMs);
      setCapturedEvents((prev) =>
        prev.map((event) =>
          event.id === eventId
            ? {
                ...event,
                durationDivisions,
                endedAtMs,
              }
            : event,
        ),
      );

      activeEvent.allNotes.forEach((note) => {
        if (activeNoteOwnerRef.current.get(note) === eventId) {
          activeNoteOwnerRef.current.delete(note);
        }
        audioMissingSinceRef.current.delete(note);
      });

      activeCapturedEventsRef.current.delete(eventId);
      lastReleasedAtMsRef.current = endedAtMs;
    },
    [quantizeDurationDivisions],
  );

  const releaseCapturedNote = useCallback(
    (note: number, endedAtMs: number, source: 'midi' | 'audio') => {
      const pendingChord = pendingChordRef.current;
      if (pendingChord && pendingChord.source === source && pendingChord.midiNotes.has(note)) {
        window.clearTimeout(pendingChord.timeoutId);
        pendingChordRef.current = null;
        const pendingNotes = normalizeMidiNotes(Array.from(pendingChord.midiNotes));
        if (pendingNotes.length > 0) {
          registerCapturedEvent(pendingNotes, pendingChord.startedAtMs, pendingChord.source);
        }
      }

      const eventId = activeNoteOwnerRef.current.get(note);
      if (!eventId) return;

      const activeEvent = activeCapturedEventsRef.current.get(eventId);
      if (!activeEvent) {
        activeNoteOwnerRef.current.delete(note);
        return;
      }

      activeEvent.activeNotes.delete(note);
      activeNoteOwnerRef.current.delete(note);
      audioMissingSinceRef.current.delete(note);

      if (activeEvent.activeNotes.size === 0) {
        finalizeCapturedEvent(eventId, endedAtMs);
      }
    },
    [finalizeCapturedEvent, registerCapturedEvent],
  );

  const getCommittedTimelineDivisions = useCallback(
    (events: LiveComposerEvent[]) =>
      events.reduce(
        (total, event) =>
          total +
          Math.max(0, event.restBeforeDivisions) +
          Math.max(MIN_EVENT_DURATION_DIVISIONS, event.durationDivisions),
        0,
      ),
    [],
  );

  const getEventStartDivisions = useCallback((events: LiveComposerEvent[], eventId: string) => {
    let cursorDivisions = 0;
    for (const event of events) {
      cursorDivisions += Math.max(0, event.restBeforeDivisions);
      if (event.id === eventId) {
        return cursorDivisions;
      }
      cursorDivisions += Math.max(MIN_EVENT_DURATION_DIVISIONS, event.durationDivisions);
    }
    return cursorDivisions;
  }, []);

  const computePedalPositionDivisions = useCallback(
    (capturedAtMs: number) => {
      const activeEvents = Array.from(activeCapturedEventsRef.current.values());
      if (activeEvents.length > 0) {
        const activeEvent = activeEvents.reduce((selected, candidate) =>
          candidate.startedAtMs > selected.startedAtMs ? candidate : selected,
        );
        return getEventStartDivisions(capturedEvents, activeEvent.id);
      }

      let positionDivisions = getCommittedTimelineDivisions(capturedEvents);
      const lastReleasedAtMs = lastReleasedAtMsRef.current;
      if (lastReleasedAtMs != null && capturedAtMs > lastReleasedAtMs) {
        positionDivisions += quantizeRestDivisions(capturedAtMs - lastReleasedAtMs);
      }
      return positionDivisions;
    },
    [capturedEvents, getCommittedTimelineDivisions, getEventStartDivisions, quantizeRestDivisions],
  );

  const registerPedalEvent = useCallback(
    (pedalDown: boolean, capturedAtMs: number) => {
      let forcedPositionDivisions: number | null = null;
      const pendingChord = pendingChordRef.current;
      if (pendingChord) {
        forcedPositionDivisions =
          getCommittedTimelineDivisions(capturedEvents) +
          computeRestBeforeDivisions(pendingChord.startedAtMs);
        window.clearTimeout(pendingChord.timeoutId);
        pendingChordRef.current = null;
        const pendingNotes = normalizeMidiNotes(Array.from(pendingChord.midiNotes));
        if (pendingNotes.length > 0) {
          registerCapturedEvent(pendingNotes, pendingChord.startedAtMs, pendingChord.source);
        }
      }
      const positionDivisions =
        forcedPositionDivisions ?? computePedalPositionDivisions(capturedAtMs);

      setCapturedPedalEvents((prev) => {
        const lastEvent = prev[prev.length - 1];
        if (
          lastEvent &&
          lastEvent.pedalDown === pedalDown &&
          lastEvent.positionDivisions === positionDivisions
        ) {
          return prev;
        }

        return [
          ...prev,
          {
            id: `live-composer-pedal-${nextEventIdRef.current++}`,
            capturedAtMs,
            source: 'midi',
            pedalDown,
            positionDivisions,
          },
        ];
      });
    },
    [
      capturedEvents,
      computePedalPositionDivisions,
      computeRestBeforeDivisions,
      getCommittedTimelineDivisions,
      registerCapturedEvent,
    ],
  );

  const stopPlayback = useCallback(
    ({
      resetProgress = false,
      hideCursor = false,
    }: { resetProgress?: boolean; hideCursor?: boolean } = {}) => {
      if (playbackNextInstructionTimeoutRef.current != null) {
        window.clearTimeout(playbackNextInstructionTimeoutRef.current);
        playbackNextInstructionTimeoutRef.current = null;
      }
      if (playbackNextInstructionDueAtMsRef.current != null && !resetProgress) {
        playbackResumeDelayMsRef.current = Math.max(
          0,
          playbackNextInstructionDueAtMsRef.current - performance.now(),
        );
      } else if (resetProgress) {
        playbackResumeDelayMsRef.current = 0;
      }
      playbackNextInstructionDueAtMsRef.current = null;
      playbackPedalDownRef.current = false;
      playbackSustainedNotesRef.current.clear();
      playbackSynthRef.current?.releaseAll();
      (window as PlaybackSamplerWindow).__museflowSampler?.releaseAll?.();
      setIsPlayingBack(false);
      if (resetProgress) {
        playbackInstructionsRef.current = [];
        playbackSnapshotSignatureRef.current = '';
        playbackNextInstructionIndexRef.current = 0;
      }
      if (hideCursor || resetProgress) {
        setShowPlaybackCursor(false);
      }
    },
    [],
  );

  const ensurePlaybackSynth = useCallback(() => {
    if (playbackSynthRef.current) return playbackSynthRef.current;

    const synth = new Tone.PolySynth(Tone.Synth).toDestination();
    synth.set({
      volume: -7,
      oscillator: {
        type: 'triangle',
      },
      envelope: {
        attack: 0.01,
        decay: 0.12,
        sustain: 0.28,
        release: 0.7,
      },
    });

    playbackSynthRef.current = synth;
    return synth;
  }, []);

  const flushPendingChord = useCallback(() => {
    const pendingChord = pendingChordRef.current;
    if (!pendingChord) return;

    window.clearTimeout(pendingChord.timeoutId);
    pendingChordRef.current = null;

    const midiNotes = normalizeMidiNotes(Array.from(pendingChord.midiNotes));
    if (midiNotes.length === 0) return;

    registerCapturedEvent(midiNotes, pendingChord.startedAtMs, pendingChord.source);
  }, [registerCapturedEvent]);

  const clearPendingChord = useCallback(() => {
    const pendingChord = pendingChordRef.current;
    if (pendingChord) {
      window.clearTimeout(pendingChord.timeoutId);
      pendingChordRef.current = null;
    }
  }, []);

  const captureInputNotes = useCallback(
    (incomingNotes: number[], capturedAtMs: number, source: 'midi' | 'audio') => {
      let midiNotes = normalizeMidiNotes(incomingNotes);
      if (midiNotes.length === 0) return;

      if (source === 'audio') {
        midiNotes = midiNotes.filter((note) => {
          const previous = recentAudioOnsetsRef.current.get(note);
          if (previous != null && capturedAtMs - previous < AUDIO_ONSET_DEDUP_MS) {
            return false;
          }
          recentAudioOnsetsRef.current.set(note, capturedAtMs);
          return true;
        });
      }

      if (midiNotes.length === 0) return;

      const pendingChord = pendingChordRef.current;
      if (pendingChord && capturedAtMs - pendingChord.startedAtMs <= CHORD_GROUP_WINDOW_MS) {
        midiNotes.forEach((note) => pendingChord.midiNotes.add(note));
        return;
      }

      flushPendingChord();

      pendingChordRef.current = {
        startedAtMs: capturedAtMs,
        midiNotes: new Set(midiNotes),
        source,
        timeoutId: window.setTimeout(() => {
          flushPendingChord();
        }, CHORD_GROUP_WINDOW_MS),
      };
    },
    [flushPendingChord],
  );

  const applyInputMode = useCallback(
    async (nextMode: PreferredInputMode) => {
      flushPendingChord();
      clearActiveCaptureState();
      setInputMode(nextMode);
      audioStartRequestRef.current += 1;

      if (nextMode === 'audio') {
        if (!audioRecognitionEnabled) {
          return;
        }

        const requestId = audioStartRequestRef.current;

        try {
          await start();
          if (audioStartRequestRef.current !== requestId) return;
        } catch {
          if (audioStartRequestRef.current !== requestId) return;
        }
        return;
      }

      void stop();
    },
    [audioRecognitionEnabled, clearActiveCaptureState, flushPendingChord, start, stop],
  );

  const handleUndo = useCallback(() => {
    stopPlayback({ resetProgress: true, hideCursor: true });
    flushPendingChord();
    const lastNoteEvent = capturedEvents[capturedEvents.length - 1];
    const lastPedalEvent = capturedPedalEvents[capturedPedalEvents.length - 1];

    if (!lastNoteEvent && !lastPedalEvent) {
      return;
    }

    if (
      lastPedalEvent &&
      (!lastNoteEvent || lastPedalEvent.capturedAtMs >= lastNoteEvent.capturedAtMs)
    ) {
      setCapturedPedalEvents((prev) => prev.slice(0, -1));
      return;
    }

    setCapturedEvents((prev) => {
      const nextEvents = prev.slice(0, -1);
      const removedEvent = prev[prev.length - 1];
      if (removedEvent) {
        activeCapturedEventsRef.current.delete(removedEvent.id);
        removedEvent.midiNotes.forEach((note) => {
          if (activeNoteOwnerRef.current.get(note) === removedEvent.id) {
            activeNoteOwnerRef.current.delete(note);
          }
          audioMissingSinceRef.current.delete(note);
        });
      }
      lastReleasedAtMsRef.current = nextEvents[nextEvents.length - 1]?.endedAtMs ?? null;
      return nextEvents;
    });
  }, [capturedEvents, capturedPedalEvents, flushPendingChord, stopPlayback]);

  const getDraftEventsSnapshot = useCallback(() => {
    const pendingChord = pendingChordRef.current;
    if (!pendingChord) return capturedEvents;

    const midiNotes = normalizeMidiNotes(Array.from(pendingChord.midiNotes));
    if (midiNotes.length === 0) return capturedEvents;

    return [
      ...capturedEvents,
      {
        id: 'live-composer-pending',
        midiNotes,
        capturedAtMs: pendingChord.startedAtMs,
        source: pendingChord.source,
        restBeforeDivisions: computeRestBeforeDivisions(pendingChord.startedAtMs),
        durationDivisions: MIN_EVENT_DURATION_DIVISIONS,
        endedAtMs: undefined,
      },
    ];
  }, [capturedEvents, computeRestBeforeDivisions]);

  const getDraftPedalEventsSnapshot = useCallback(() => capturedPedalEvents, [capturedPedalEvents]);

  const getDraftEventsSignature = useCallback(
    (events: LiveComposerEvent[], pedalEvents: LiveComposerPedalEvent[]) =>
      `${events.length}:${events
        .map(
          (event) =>
            `${event.id}:${event.restBeforeDivisions}:${event.durationDivisions}:${event.midiNotes.join(',')}`,
        )
        .join('|')}::${pedalEvents
        .map((event) => `${event.id}:${event.positionDivisions}:${event.pedalDown ? 1 : 0}`)
        .join('|')}`,
    [],
  );

  const handleClear = useCallback(() => {
    stopPlayback({ resetProgress: true, hideCursor: true });
    clearPendingChord();
    clearActiveCaptureState();
    setCapturedEvents([]);
    setCapturedPedalEvents([]);
    recentAudioOnsetsRef.current.clear();
  }, [clearActiveCaptureState, clearPendingChord, stopPlayback]);

  const triggerPlaybackAttack = useCallback(
    (midiNotes: number[], velocity: number) => {
      const playbackWindow = window as PlaybackSamplerWindow;
      const sampler = playbackWindow.__museflowSampler;
      const synth = sampler ? null : ensurePlaybackSynth();
      const noteNames = midiNotes.map((note) => midiToName(note));

      if (sampler) {
        noteNames.forEach((note) => {
          sampler.triggerAttack(note, Tone.immediate(), velocity);
        });
        return;
      }

      const frequencies = midiNotes.map((note) => Tone.Frequency(note, 'midi').toFrequency());
      frequencies.forEach((frequency) => {
        synth?.triggerAttack(frequency, Tone.immediate(), velocity);
      });
    },
    [ensurePlaybackSynth],
  );

  const triggerPlaybackRelease = useCallback(
    (midiNotes: number[]) => {
      const playbackWindow = window as PlaybackSamplerWindow;
      const sampler = playbackWindow.__museflowSampler;
      const synth = sampler ? null : ensurePlaybackSynth();
      const noteNames = midiNotes.map((note) => midiToName(note));

      if (sampler) {
        noteNames.forEach((note) => {
          sampler.triggerRelease(note, Tone.immediate());
        });
        return;
      }

      const frequencies = midiNotes.map((note) => Tone.Frequency(note, 'midi').toFrequency());
      frequencies.forEach((frequency) => {
        synth?.triggerRelease(frequency, Tone.immediate());
      });
    },
    [ensurePlaybackSynth],
  );

  const releasePlaybackSustainedNotes = useCallback(() => {
    const sustainedNotes = Array.from(playbackSustainedNotesRef.current);
    if (sustainedNotes.length === 0) return;
    triggerPlaybackRelease(sustainedNotes);
    playbackSustainedNotesRef.current.clear();
  }, [triggerPlaybackRelease]);

  const buildPlaybackInstructions = useCallback(
    (noteEvents: LiveComposerEvent[], pedalEvents: LiveComposerPedalEvent[]) => {
      const instructions: PlaybackInstruction[] = [];
      let cursorDivisions = 0;

      noteEvents.forEach((event) => {
        cursorDivisions += Math.max(0, event.restBeforeDivisions);
        const startDivisions = cursorDivisions;
        instructions.push({
          id: `${event.id}:on`,
          atDivisions: startDivisions,
          type: 'note-on',
          midiNotes: event.midiNotes,
          source: event.source,
        });
        instructions.push({
          id: `${event.id}:off`,
          atDivisions:
            startDivisions + Math.max(MIN_EVENT_DURATION_DIVISIONS, event.durationDivisions),
          type: 'note-off',
          midiNotes: event.midiNotes,
          source: event.source,
        });
        cursorDivisions += Math.max(MIN_EVENT_DURATION_DIVISIONS, event.durationDivisions);
      });

      pedalEvents.forEach((event) => {
        instructions.push({
          id: event.id,
          atDivisions: Math.max(0, event.positionDivisions),
          type: 'pedal',
          pedalDown: event.pedalDown,
        });
      });

      const priority = (instruction: PlaybackInstruction) => {
        if (instruction.type === 'pedal' && instruction.pedalDown) return 0;
        if (instruction.type === 'pedal' && !instruction.pedalDown) return 1;
        if (instruction.type === 'note-on') return 2;
        return 3;
      };

      return instructions.sort(
        (a, b) => a.atDivisions - b.atDivisions || priority(a) - priority(b),
      );
    },
    [],
  );

  const playInstructionAtIndex = useCallback(
    async (instructionIndex: number) => {
      const instructions = playbackInstructionsRef.current;
      if (instructionIndex >= instructions.length) {
        playbackNextInstructionIndexRef.current = instructions.length;
        playbackResumeDelayMsRef.current = 0;
        playbackNextInstructionDueAtMsRef.current = null;
        playbackNextInstructionTimeoutRef.current = null;
        playbackPedalDownRef.current = false;
        releasePlaybackSustainedNotes();
        setIsPlayingBack(false);
        return;
      }

      const instruction = instructions[instructionIndex];

      if (instruction.type === 'pedal') {
        playbackPedalDownRef.current = instruction.pedalDown;
        if (!instruction.pedalDown) {
          releasePlaybackSustainedNotes();
        }
      } else if (instruction.type === 'note-on') {
        instruction.midiNotes.forEach((note) => {
          if (playbackSustainedNotesRef.current.has(note)) {
            triggerPlaybackRelease([note]);
            playbackSustainedNotesRef.current.delete(note);
          }
        });
        const velocity = instruction.source === 'audio' ? 0.82 : 0.92;
        triggerPlaybackAttack(instruction.midiNotes, velocity);
      } else {
        if (playbackPedalDownRef.current) {
          instruction.midiNotes.forEach((note) => playbackSustainedNotesRef.current.add(note));
        } else {
          triggerPlaybackRelease(instruction.midiNotes);
        }
      }

      setShowPlaybackCursor(true);
      playbackNextInstructionIndexRef.current = instructionIndex + 1;

      if (instructionIndex + 1 >= instructions.length) {
        playbackResumeDelayMsRef.current = 0;
        playbackNextInstructionDueAtMsRef.current = null;
        playbackNextInstructionTimeoutRef.current = null;
        playbackPedalDownRef.current = false;
        releasePlaybackSustainedNotes();
        setIsPlayingBack(false);
        return;
      }

      const nextInstruction = instructions[instructionIndex + 1];
      const eventDurationMs = Math.round(
        Math.max(0, nextInstruction.atDivisions - instruction.atDivisions) *
          getDivisionDurationSeconds(playbackTempoRef.current) *
          1000,
      );
      playbackResumeDelayMsRef.current = eventDurationMs;
      playbackNextInstructionDueAtMsRef.current = performance.now() + eventDurationMs;
      playbackNextInstructionTimeoutRef.current = window.setTimeout(() => {
        void playInstructionAtIndex(playbackNextInstructionIndexRef.current);
      }, eventDurationMs);
    },
    [
      getDivisionDurationSeconds,
      releasePlaybackSustainedNotes,
      triggerPlaybackAttack,
      triggerPlaybackRelease,
    ],
  );

  const startPlayback = useCallback(
    async ({
      noteEventsToPlay,
      pedalEventsToPlay,
      fromStart,
    }: {
      noteEventsToPlay: LiveComposerEvent[];
      pedalEventsToPlay: LiveComposerPedalEvent[];
      fromStart: boolean;
    }) => {
      const instructions = buildPlaybackInstructions(noteEventsToPlay, pedalEventsToPlay);
      if (instructions.length === 0) return;

      await Tone.start();
      const playbackWindow = window as PlaybackSamplerWindow;
      await playbackWindow.__ensureSamplerReady?.();

      playbackInstructionsRef.current = instructions;
      playbackSnapshotSignatureRef.current = getDraftEventsSignature(
        noteEventsToPlay,
        pedalEventsToPlay,
      );
      playbackPedalDownRef.current = false;
      playbackSustainedNotesRef.current.clear();

      if (fromStart || playbackNextInstructionIndexRef.current >= instructions.length) {
        playbackNextInstructionIndexRef.current = 0;
        playbackResumeDelayMsRef.current = 0;
        setShowPlaybackCursor(true);
      }

      setIsPlayingBack(true);

      const nextInstructionIndex = playbackNextInstructionIndexRef.current;
      const resumeDelayMs = fromStart ? 0 : Math.max(0, playbackResumeDelayMsRef.current);

      if (resumeDelayMs === 0) {
        await playInstructionAtIndex(nextInstructionIndex);
        return;
      }

      playbackNextInstructionDueAtMsRef.current = performance.now() + resumeDelayMs;
      playbackNextInstructionTimeoutRef.current = window.setTimeout(() => {
        void playInstructionAtIndex(playbackNextInstructionIndexRef.current);
      }, resumeDelayMs);
    },
    [buildPlaybackInstructions, getDraftEventsSignature, playInstructionAtIndex],
  );

  const handlePlaybackToggle = useCallback(async () => {
    const noteEventsToPlay = getDraftEventsSnapshot();
    const pedalEventsToPlay = getDraftPedalEventsSnapshot();
    flushPendingChord();

    if (isPlayingBack) {
      stopPlayback();
      return;
    }

    const nextSignature = getDraftEventsSignature(noteEventsToPlay, pedalEventsToPlay);
    const shouldResume =
      playbackSnapshotSignatureRef.current === nextSignature &&
      playbackNextInstructionIndexRef.current > 0 &&
      playbackNextInstructionIndexRef.current < playbackInstructionsRef.current.length;

    await startPlayback({
      noteEventsToPlay,
      pedalEventsToPlay,
      fromStart: !shouldResume,
    });
  }, [
    flushPendingChord,
    getDraftEventsSignature,
    getDraftEventsSnapshot,
    getDraftPedalEventsSnapshot,
    isPlayingBack,
    startPlayback,
    stopPlayback,
  ]);

  const handlePlaybackRestart = useCallback(async () => {
    const noteEventsToPlay = getDraftEventsSnapshot();
    const pedalEventsToPlay = getDraftPedalEventsSnapshot();
    flushPendingChord();
    stopPlayback({ resetProgress: true });
    await startPlayback({
      noteEventsToPlay,
      pedalEventsToPlay,
      fromStart: true,
    });
  }, [
    flushPendingChord,
    getDraftEventsSnapshot,
    getDraftPedalEventsSnapshot,
    startPlayback,
    stopPlayback,
  ]);

  useEffect(() => {
    setRecognitionHints(null);
    return () => {
      setRecognitionHints(null);
    };
  }, [setRecognitionHints]);

  useEffect(() => {
    if (!hasInitializedInputModeRef.current) {
      hasInitializedInputModeRef.current = true;
      void applyInputMode(preferredInputMode);
      return;
    }
    if (preferredInputMode === inputMode) return;
    void applyInputMode(preferredInputMode);
  }, [applyInputMode, inputMode, preferredInputMode]);

  useEffect(() => {
    if (inputMode !== 'midi') return;

    const listener: Listener = {
      name: MIDI_LISTENER_NAME,
      noteOn: (event) => {
        captureInputNotes([event.note], performance.now(), 'midi');
      },
      noteOff: (event) => {
        releaseCapturedNote(event.note, performance.now(), 'midi');
      },
      pedalChange: (event: PedalMessage) => {
        registerPedalEvent(event.down, performance.now());
      },
    };

    addMidiListener(listener);

    return () => {
      removeMidiListener(listener);
    };
  }, [
    addMidiListener,
    captureInputNotes,
    inputMode,
    registerPedalEvent,
    releaseCapturedNote,
    removeMidiListener,
  ]);

  useEffect(() => {
    if (inputMode !== 'audio') return;

    const listener = {
      name: AUDIO_LISTENER_NAME,
      onFrame: (frame: RecognitionFrame) => {
        const frameTimeMs = frame.timeSec * 1000;
        const onsetMidiNotes = normalizeMidiNotes(frame.onsetMidiNotes ?? []);
        const stableMidiNotes = new Set(normalizeMidiNotes(frame.midiNotes ?? []));

        if (onsetMidiNotes.length > 0) {
          captureInputNotes(onsetMidiNotes, frameTimeMs, 'audio');
        }

        activeCapturedEventsRef.current.forEach((activeEvent) => {
          if (activeEvent.source !== 'audio') return;

          activeEvent.allNotes.forEach((note) => {
            if (stableMidiNotes.has(note)) {
              audioMissingSinceRef.current.delete(note);
              return;
            }

            const missingSince = audioMissingSinceRef.current.get(note);
            if (missingSince == null) {
              audioMissingSinceRef.current.set(note, frameTimeMs);
              return;
            }

            if (frameTimeMs - missingSince >= AUDIO_RELEASE_GRACE_MS) {
              releaseCapturedNote(note, frameTimeMs, 'audio');
            }
          });
        });
      },
    };

    addAudioListener(listener);

    return () => {
      removeAudioListener(listener);
    };
  }, [addAudioListener, captureInputNotes, inputMode, releaseCapturedNote, removeAudioListener]);

  useEffect(() => {
    return () => {
      audioStartRequestRef.current += 1;
      stopPlayback({ resetProgress: true, hideCursor: true });
      clearPendingChord();
      clearActiveCaptureState();
      void stop();
      playbackSynthRef.current?.dispose();
      playbackSynthRef.current = null;
    };
  }, [clearActiveCaptureState, clearPendingChord, stop, stopPlayback]);

  const recentCaptures = useMemo(
    () =>
      [
        ...capturedEvents.map((event) => ({
          id: event.id,
          capturedAtMs: event.capturedAtMs,
          source: event.source,
          label: formatChordLabel(event.midiNotes),
        })),
        ...capturedPedalEvents.map((event) => ({
          id: event.id,
          capturedAtMs: event.capturedAtMs,
          source: event.source,
          label: event.pedalDown ? PEDAL_LABEL_DOWN : PEDAL_LABEL_UP,
        })),
      ]
        .sort((a, b) => b.capturedAtMs - a.capturedAtMs)
        .slice(0, 8),
    [capturedEvents, capturedPedalEvents],
  );
  const scoreBuild = useMemo(
    () => buildLiveComposerMusicXml(capturedEvents, capturedPedalEvents),
    [capturedEvents, capturedPedalEvents],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        width: '100vw',
      }}
    >
      <Menu selected={'Repertoire'} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: 'calc(100% - var(--app-sidebar-width, 100px))',
        }}
      >
        <PageNav selected={'Create'} />
        <Box
          sx={{
            height: 'calc(100vh - 100px)',
            overflowY: 'auto',
            background:
              'radial-gradient(circle at top left, rgba(111, 135, 241, 0.16) 0%, rgba(111, 135, 241, 0) 32%), linear-gradient(180deg, #08111E 0%, #060C15 100%)',
            padding: { xs: '18px 16px 28px', md: '24px 24px 32px' },
          }}
        >
          <Box
            sx={{
              maxWidth: '1440px',
              marginX: 'auto',
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', xl: '360px minmax(0, 1fr)' },
              gap: '24px',
              alignItems: 'start',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '18px',
              }}
            >
              <Box sx={panelStyles}>
                <Box sx={eyebrowStyles}>Repertoire Create</Box>
                <Box sx={panelTitleStyles}>Capture notes and write a live draft.</Box>
                <Box sx={panelBodyStyles}>
                  Captured notes, rests, and sustain pedal changes are written into a live draft on
                  the blank score. The input source follows your saved setting from Keyboard
                  Settings.
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    gap: '10px',
                    flexWrap: 'wrap',
                    marginTop: '16px',
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={handleUndo}
                    disabled={capturedEvents.length === 0 && capturedPedalEvents.length === 0}
                    sx={actionButtonStyles}
                  >
                    Undo Last
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleClear}
                    disabled={capturedEvents.length === 0 && capturedPedalEvents.length === 0}
                    sx={secondaryActionButtonStyles}
                  >
                    Clear Score
                  </Button>
                </Box>
              </Box>

              <Box sx={panelStyles}>
                <Box sx={panelTitleStyles}>Recent Capture</Box>
                <Box sx={panelBodyStyles}>
                  Note durations, rests, and pedal changes are quantized to sixteenth-note units in
                  4/4.
                </Box>

                <Box
                  sx={{
                    marginTop: '16px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '10px',
                  }}
                >
                  {recentCaptures.length > 0 ? (
                    recentCaptures.map((event) => (
                      <Box
                        key={event.id}
                        sx={{
                          borderRadius: '999px',
                          padding: '9px 12px',
                          backgroundColor:
                            event.label === PEDAL_LABEL_DOWN || event.label === PEDAL_LABEL_UP
                              ? 'rgba(183, 167, 255, 0.14)'
                              : event.source === 'audio'
                                ? 'rgba(121, 216, 255, 0.14)'
                                : 'rgba(255, 210, 125, 0.14)',
                          border:
                            event.label === PEDAL_LABEL_DOWN || event.label === PEDAL_LABEL_UP
                              ? '1px solid rgba(183, 167, 255, 0.24)'
                              : event.source === 'audio'
                                ? '1px solid rgba(121, 216, 255, 0.24)'
                                : '1px solid rgba(255, 210, 125, 0.22)',
                          color: '#EAF2FF',
                          fontSize: '12px',
                          fontWeight: 700,
                          letterSpacing: '0.02em',
                        }}
                      >
                        {event.label}
                      </Box>
                    ))
                  ) : (
                    <Box sx={{ color: 'rgba(226, 234, 255, 0.62)', fontSize: '12px' }}>
                      No notes or pedal events captured yet.
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            <LiveComposerSheet
              musicXml={scoreBuild.xml}
              measureCount={scoreBuild.measureCount}
              noteCount={capturedEvents.length + capturedPedalEvents.length}
              playbackTempo={playbackTempo}
              canPlay={
                getDraftEventsSnapshot().length > 0 || getDraftPedalEventsSnapshot().length > 0
              }
              isPlayingBack={isPlayingBack}
              showPlaybackCursor={showPlaybackCursor}
              onTempoChange={setPlaybackTempo}
              onPlaybackToggle={() => {
                void handlePlaybackToggle();
              }}
              onPlaybackRestart={() => {
                void handlePlaybackRestart();
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const StatPill = ({ label, value }: { label: string; value: string }) => (
  <Box
    sx={{
      borderRadius: '999px',
      backgroundColor: 'rgba(9, 19, 36, 0.06)',
      color: '#10203A',
      padding: '9px 12px',
      minWidth: '96px',
    }}
  >
    <Box
      sx={{
        fontSize: '10px',
        fontWeight: 800,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: 'rgba(16, 32, 58, 0.52)',
      }}
    >
      {label}
    </Box>
    <Box
      sx={{
        fontSize: '16px',
        fontWeight: 700,
        marginTop: '2px',
      }}
    >
      {value}
    </Box>
  </Box>
);

const panelStyles = {
  borderRadius: '22px',
  border: '1px solid rgba(121, 216, 255, 0.14)',
  background: 'linear-gradient(180deg, rgba(10, 18, 31, 0.96) 0%, rgba(7, 12, 22, 0.99) 100%)',
  boxShadow: '0 18px 34px rgba(0, 0, 0, 0.2)',
  padding: '20px',
};

const panelTitleStyles = {
  color: '#F2F6FF',
  fontSize: '22px',
  fontWeight: 700,
  lineHeight: 1.2,
};

const panelBodyStyles = {
  color: 'rgba(226, 234, 255, 0.74)',
  fontSize: '13px',
  lineHeight: 1.65,
  marginTop: '8px',
};

const eyebrowStyles = {
  color: '#79D8FF',
  fontSize: '11px',
  fontWeight: 800,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  marginBottom: '8px',
};

const actionButtonStyles = {
  background: 'linear-gradient(90deg, #FFD27D 0%, #FFB95E 100%)',
  color: '#1D1303',
  fontWeight: 800,
  borderRadius: '999px',
  paddingX: '16px',
  '&:hover': {
    background: 'linear-gradient(90deg, #FFD88F 0%, #FFC46F 100%)',
  },
};

const secondaryActionButtonStyles = {
  color: '#EAF2FF',
  borderColor: 'rgba(255,255,255,0.16)',
  borderRadius: '999px',
  paddingX: '16px',
  '&:hover': {
    borderColor: 'rgba(121, 216, 255, 0.28)',
    backgroundColor: 'rgba(121, 216, 255, 0.08)',
  },
};

export default Create;
